<template>
  <AppSidebar
    v-model="drawerIsOpen"
    :mini="computedMini"
    :permanent="permanent"
  >
    <div class="projects-sidebar">
      <v-skeleton-loader
        v-if="getProjectsFoldersTreePending"
        type="list-item, list-item, list-item"
        class="projects-sidebar__loader"
      />
      <div v-else class="projects-sidebar__nav app-sidebar-menu">
        <AppSidebarItem
          v-for="item in sidebarItems"
          :key="item.key"
          :item="item"
          :icon-only="mini"
          @select="onSelect"
        />
      </div>
    </div>
  </AppSidebar>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue'
import { mapActions, mapMutations, mapState } from 'vuex'

import type { AppSidebarItemData } from '@/common/app-sidebar/appSidebar.types'
import AppSidebar from '@/common/app-sidebar/AppSidebar.vue'
import AppSidebarItem from '@/common/app-sidebar/AppSidebarItem.vue'
import { FOLDER_ROUTE_NAME, ROOMS_ROUTE_NAME } from '@/router/routerData'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'
import { LOAD_PROJECTS_FOLDERS_TREE } from '@/store/modules/projects-folders/action_types'

const CONTENT_REFLOW_DELAY = 190

type HomeFolder = {
  id: number | string
  name: string
  numbering?: number
  children?: HomeFolder[]
}

type HomeFolderWithDepth = HomeFolder & {
  depth: number
}

export default defineComponent({
  name: 'ProjectsSidebar',
  components: {
    AppSidebar,
    AppSidebarItem,
  },
  data () {
    return {
      expandedFolderIds: [] as string[],
      permanent: false,
    }
  },
  computed: {
    ...mapState('global', ['mini', 'toggleProjectDrawer']),
    ...mapState('projectsFolders', ['projectsFoldersTree', 'getProjectsFoldersTreePending', 'currentProjectsFolder']),
    drawerIsOpen: {
      get () {
        return this.$vuetify.display.mobile
          ? this.toggleProjectDrawer
          : true
      },
      set (val: boolean) {
        this.SET_TOGGLE_PROJECT_DRAWER(val)
      },
    },
    computedMini: {
      get () {
        return this.mini
      },
      set (val: boolean) {
        this.SET_MINI(val)
      },
    },
    visibleFolders (): HomeFolderWithDepth[] {
      return this.flattenVisibleFolders(this.projectsFoldersTree)
    },
    sidebarItems (): AppSidebarItemData[] {
      const baseItems: AppSidebarItemData[] = [
        {
          key: 'my-projects',
          label: this.$t('projects.ProjectsHeader.myProjects').toString(),
          icon: 'home_storage',
          kind: 'nav',
          active: this.isProjectsActive,
          dataLocator: 'my-projects-nav-item',
        },
        {
          key: 'recent-projects',
          label: this.$t('projects.ProjectsHeader.recentProjects').toString(),
          icon: 'schedule',
          kind: 'nav',
          active: this.isRecentActive,
          dataLocator: 'recents-nav-item',
        },
        {
          key: 'my-folders',
          label: this.$t('projects.ProjectsHeader.myFolders').toString(),
          icon: 'folder',
          kind: 'section',
          active: this.isFoldersActive,
          dataLocator: 'my-folders-nav-item',
        },
      ]

      return baseItems.concat(
        this.visibleFolders.map((folder): AppSidebarItemData => ({
          key: `folder-${folder.id}`,
          label: folder.numbering ? `${folder.numbering} ${folder.name}` : folder.name,
          icon: this.expandedFolderIds.includes(`${folder.id}`) ? 'folder-open' : 'folder',
          iconWeight: 'fas',
          iconColor: '#178EE3',
          kind: 'folder',
          active: `${this.currentProjectsFolder?.id ?? ''}` === `${folder.id}`,
          dataLocator: 'folder-nav-item',
          depth: folder.depth,
        })),
      )
    },
    isRecentActive (): boolean {
      return this.$route.params['childId'] === 'recent' || this.$route.params['folderId'] === 'recent'
    },
    isProjectsActive (): boolean {
      return !this.isRecentActive && !this.currentProjectsFolder?.id
    },
    isFoldersActive (): boolean {
      return !!this.currentProjectsFolder?.id
    },
  },
  watch: {
    '$vuetify.display.mobile': {
      async handler (newValue) {
        await nextTick()

        setTimeout(() => {
          this.permanent = !newValue
          if (newValue) {
            this.computedMini = false
          }
        }, CONTENT_REFLOW_DELAY)
      },
      immediate: true,
    },
    computedMini: {
      async handler () {
        if (!this.$vuetify.display.mobile) {
          await nextTick()
          setTimeout(() => {
            this.permanent = !this.permanent
          }, CONTENT_REFLOW_DELAY)
          this.permanent = !this.permanent
        }
      },
      immediate: true,
    },
    currentProjectsFolder: {
      handler (folder: HomeFolder | null) {
        if (!folder?.id) {
          return
        }

        this.expandFolderPath(`${folder.id}`)
      },
      immediate: true,
    },
  },
  async mounted () {
    await this.LOAD_PROJECTS_FOLDERS_TREE()
  },
  methods: {
    ...mapActions('projectsFolders', [LOAD_PROJECTS_FOLDERS_TREE]),
    ...mapMutations('global', [GlobalMutationTypes.SET_MINI, GlobalMutationTypes.SET_TOGGLE_PROJECT_DRAWER]),
    flattenVisibleFolders (folders: HomeFolder[] = [], depth = 0): HomeFolderWithDepth[] {
      return folders.flatMap((folder) => {
        const currentFolder: HomeFolderWithDepth = {
          ...folder,
          depth,
        }

        const folderId = `${folder.id}`
        const shouldShowChildren = this.expandedFolderIds.includes(folderId)

        return [
          currentFolder,
          ...(shouldShowChildren ? this.flattenVisibleFolders(folder.children || [], depth + 1) : []),
        ]
      })
    },
    findFolderPath (folders: HomeFolder[] = [], targetFolderId: string, parentPath: string[] = []): string[] {
      for (const folder of folders) {
        const currentPath = parentPath.concat(`${folder.id}`)
        if (`${folder.id}` === targetFolderId) {
          return currentPath
        }

        const childPath = this.findFolderPath(folder.children || [], targetFolderId, currentPath)
        if (childPath.length) {
          return childPath
        }
      }

      return []
    },
    findFolderById (folders: HomeFolder[] = [], targetFolderId: string): HomeFolder | null {
      for (const folder of folders) {
        if (`${folder.id}` === targetFolderId) {
          return folder
        }

        const childFolder = this.findFolderById(folder.children || [], targetFolderId)
        if (childFolder) {
          return childFolder
        }
      }

      return null
    },
    expandFolderPath (folderId: string) {
      const folderPath = this.findFolderPath(this.projectsFoldersTree, folderId)
      const parentIds = folderPath.slice(0, -1)

      this.expandedFolderIds = Array.from(new Set([...this.expandedFolderIds, ...parentIds]))
    },
    onSelect (key: string) {
      if (key === 'my-projects') {
        if (this.$route.name !== ROOMS_ROUTE_NAME) {
          this.$router.push({ name: ROOMS_ROUTE_NAME })
        }
        return
      }

      if (key === 'recent-projects') {
        if (this.$route.params['folderId'] !== 'recent') {
          this.$router.push({
            name: FOLDER_ROUTE_NAME,
            params: { folderId: 'recent' },
          })
        }
        return
      }

      if (key.startsWith('folder-')) {
        const folderId = key.replace('folder-', '')
        const isExpanded = this.expandedFolderIds.includes(folderId)
        const folder = this.findFolderById(this.projectsFoldersTree, folderId)
        const hasChildren = !!folder?.children?.length

        if (hasChildren) {
          this.expandFolderPath(folderId)
          this.expandedFolderIds = isExpanded
            ? this.expandedFolderIds.filter(id => id !== folderId)
            : [...this.expandedFolderIds, folderId]
        }

        if (`${this.currentProjectsFolder?.id ?? ''}` !== folderId) {
          this.$router.push({
            name: FOLDER_ROUTE_NAME,
            params: { folderId },
          })
        }
      }
    },
  },
})
</script>

<style scoped lang="scss">
.projects-sidebar {
  overflow-y: auto;

  &__loader {
    padding: 8px;

    :deep(.v-skeleton-loader__list-item) {
      background: transparent;
      height: 40px;
    }
  }
}

@media (max-width: 959px) {
  .projects-sidebar {
    margin-top: 0;
    padding-top: 0;
  }
}
</style>
